import { Button, Text } from '@components/ui';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';

import { PaymentCardSkeleton } from './payment-card-skeleton';
import type { SavedCard } from './paymentTypes';

interface SavedCardsProps {
  cards: SavedCard[];
  loading: boolean;
  selectedToken: string | null;
  selectingToken?: string | null;
  deletingToken?: string | null;
  busy?: boolean;
  canAdd: boolean;
  onAdd: () => void;
  onSelect: (token: string) => void;
  onDelete: (token: string) => void;
}

interface CardRowProps {
  card: SavedCard;
  selected: boolean;
  selecting: boolean;
  deleting: boolean;
  busy: boolean;
  onSelect: (token: string) => void;
  onDelete: (token: string) => void;
}

const CardRow = ({
  card,
  selected,
  selecting,
  deleting,
  busy,
  onSelect,
  onDelete,
}: CardRowProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: { xs: 2, md: 2.5 },
        py: 1.75,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: selected || selecting ? 'action.hover' : 'transparent',
        opacity: deleting ? 0.55 : 1,
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      {card.imageUrl ? (
        <Box
          component="img"
          src={card.imageUrl}
          alt=""
          sx={{
            height: 28,
            width: 40,
            flexShrink: 0,
            objectFit: 'contain',
            opacity: 0.9,
          }}
        />
      ) : (
        <Box
          sx={{
            width: 40,
            height: 28,
            flexShrink: 0,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Text variant="label" sx={{ fontSize: 14 }}>
            {card.cardType} •••• {card.last4}
          </Text>
          {card.default ? (
            <Box
              component="span"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'action.selected',
                color: 'primary.main',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.02em',
              }}
            >
              {t('common.default')}
            </Box>
          ) : null}
        </Box>
        <Text variant="caption" tone="muted" sx={{ display: 'block', mt: 0.25 }}>
          {t('payments.expires', { date: card.expirationDate })}
          {card.cardholderName ? ` · ${card.cardholderName}` : ''}
        </Text>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
        <IconButton
          size="small"
          aria-label={t('payments.selectCard')}
          aria-pressed={selected}
          aria-busy={selecting}
          disabled={busy || selected}
          onClick={() => onSelect(card.token)}
          sx={{
            color: selected || selecting ? 'primary.main' : 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {selecting ? (
            <CircularProgress size={18} color="inherit" />
          ) : selected ? (
            <RadioButtonCheckedIcon sx={{ fontSize: 20 }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
        <IconButton
          size="small"
          aria-label={t('payments.removeCard')}
          aria-busy={deleting}
          disabled={busy}
          onClick={() => onDelete(card.token)}
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover', color: 'error.main' },
          }}
        >
          {deleting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <DeleteOutlineIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export const SavedCards = ({
  cards,
  loading,
  selectedToken,
  selectingToken = null,
  deletingToken = null,
  busy = false,
  canAdd,
  onAdd,
  onSelect,
  onDelete,
}: SavedCardsProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1120,
        mx: 'auto',
        px: { xs: 2, md: 4 },
        py: 3,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Text as="h1" variant="subtitle" sx={{ fontWeight: 700 }}>
          {t('payments.title')}
        </Text>
        <Button
          type="button"
          variant="outlined"
          size="sm"
          onClick={onAdd}
          disabled={!canAdd}
          startIcon={<AddCardOutlinedIcon sx={{ fontSize: 16 }} />}
        >
          {cards.length === 0 ? t('payments.addCard') : t('payments.addAnother')}
        </Button>
      </Box>

      <Box
        sx={{
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
          bgcolor: 'background.paper',
        }}
      >
        {loading ? (
          <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'grid', gap: 1.5 }}>
            <PaymentCardSkeleton />
          </Box>
        ) : null}

        {!loading && cards.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 6,
              textAlign: 'center',
            }}
          >
            <AddCardOutlinedIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Text variant="label" sx={{ fontSize: 15 }}>
              {t('payments.empty')}
            </Text>
            <Text
              variant="caption"
              tone="muted"
              sx={{ maxWidth: 280, display: 'block' }}
            >
              {t('payments.emptyHint')}
            </Text>
            <Button
              type="button"
              variant="primary"
              onClick={onAdd}
              disabled={!canAdd}
              sx={{ mt: 1.5 }}
            >
              {t('payments.addCard')}
            </Button>
          </Box>
        ) : null}

        {!loading && cards.length > 0 ? (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {cards.map((card) => (
              <CardRow
                key={card.token}
                card={card}
                selected={selectedToken === card.token}
                selecting={selectingToken === card.token}
                deleting={deletingToken === card.token}
                busy={busy}
                onSelect={onSelect}
                onDelete={onDelete}
              />
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
